import styled, { keyframes } from 'styled-components'
import { darkOrange, orange, black, textMuted, white, warmTint, border2 } from '@/theme/colors'
import { shadows, radii, motion } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { Button } from '@components/Button'

/**
 * The shared form CTA minus its `transition: all` — the survey CTA flips
 * between disabled/enabled as options toggle, and animating that read as a
 * distracting fade here. Other modals keep the shared behavior.
 */
export const SurveyCta = styled(Button)`
	transition: none;
`

const optionIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(8px);
	}
	to {
		opacity: 1;
		transform: none;
	}
`

export const OptionList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 4px 0;
`

/** One survey answer card: icon chip + text, fading up with a light stagger. */
export const OptionButton = styled.button<{ $selected: boolean; $index: number }>`
	display: flex;
	align-items: center;
	gap: 14px;
	width: 100%;
	padding: 14px 16px;
	border-radius: ${radii.inner};
	border: 1px solid ${({ $selected }) => ($selected ? orange : border2)};
	background-color: ${({ $selected }) => ($selected ? warmTint : white)};
	cursor: pointer;
	text-align: left;
	animation: ${optionIn} 0.28s ease-out both;
	animation-delay: ${({ $index }) => $index * 45}ms;
	transition:
		border-color ${motion.fast},
		background-color ${motion.fast},
		box-shadow ${motion.fast};

	&:hover {
		border-color: ${orange};
		box-shadow: ${shadows.focus};
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

export const OptionIconChip = styled.span<{ $selected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	flex-shrink: 0;
	border-radius: ${radii.inner};
	background: ${({ $selected }) => ($selected ? orange : warmTint)};
	color: ${({ $selected }) => ($selected ? white : darkOrange)};
	transition:
		background-color ${motion.fast},
		color ${motion.fast};
`

export const OptionTexts = styled.span`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
`

export const OptionTitle = styled.span`
	font-size: ${fontSizes.body};
	font-weight: 700;
	color: ${black};
`

export const OptionDescription = styled.span`
	font-size: ${fontSizes.small};
	color: ${textMuted};
`
